import React from "react";
import { useTranslation } from "react-i18next";
import { Headset, RadioTower, MonitorCheck, ArrowRight } from "lucide-react";
import "./TelealarmeISSIntro.css";

/**
 * Section 02 — Bénéfices Téléalarme ISS.
 * Contenu volontairement localisé ici pour ne pas modifier les fichiers i18n validés.
 * Les sections suivantes (matériel, fonctionnement, spécifications...) restent inchangées.
 */
const ISS_BENEFITS = {
  fr: {
    eyebrow: "UN BESOIN ESSENTIEL, TROIS BÉNÉFICES",
    title: "Une solution pensée pour les situations qui comptent.",
    lead: "De l'appel d'urgence à l'exploitation, mettez la communication au centre de votre installation.",
    cards: [
      {
        id: "01",
        kind: "assistance",
        title: "Facilitez la communication d'urgence.",
        description: "Lorsqu'une alarme est déclenchée, le système permet la mise en relation avec l'assistance, sous réserve de ses conditions de fonctionnement.",
        label: "ASSISTANCE"
      },
      {
        id: "02",
        kind: "modernisation",
        title: "Préparez l'évolution de vos installations.",
        description: "Adoptez une architecture 4G/IP adaptée à la modernisation des communications d'urgence des ascenseurs.",
        label: "MODERNISATION"
      },
      {
        id: "03",
        kind: "supervision",
        title: "Gardez de la visibilité sur vos équipements.",
        description: "Selon les options déployées, retrouvez les événements et alertes utiles à la surveillance de vos installations.",
        label: "EXPLOITATION"
      }
    ],
    button: "Découvrir les équipements"
  },
  en: {
    eyebrow: "ONE ESSENTIAL NEED, THREE BENEFITS",
    title: "A solution designed for situations that matter.",
    lead: "From emergency calls to day-to-day operations, keep communication at the heart of your installation.",
    cards: [
      {
        id: "01",
        kind: "assistance",
        title: "Make emergency communication easier.",
        description: "When an alarm is triggered, the system can connect passengers with an assistance service, subject to operating conditions.",
        label: "ASSISTANCE"
      },
      {
        id: "02",
        kind: "modernisation",
        title: "Prepare your installations for change.",
        description: "Adopt a suitable 4G/IP architecture to modernize elevator emergency communications.",
        label: "MODERNIZATION"
      },
      {
        id: "03",
        kind: "supervision",
        title: "Keep your equipment in view.",
        description: "Depending on the options deployed, access events and alerts useful for monitoring your installations.",
        label: "OPERATIONS"
      }
    ],
    button: "Discover the equipment"
  }
};

/**
 * Decorative Lucide icons; preserve the validated dimensions and CSS.
 * Use a light stroke consistent with other approved UniTech sections.
 */
function ISSBenefitIcon({ kind }) {
  const iconProps = {
    width: 29,
    height: 29,
    strokeWidth: 1.65,
    "aria-hidden": true,
    focusable: "false"
  };

  if (kind === "assistance") return <Headset {...iconProps} />;
  if (kind === "modernisation") return <RadioTower {...iconProps} />;
  return <MonitorCheck {...iconProps} />;
}

function ISSBenefits({ data }) {
  return (
    <div className="iss-benefits">
      <header className="iss-benefits__header">
        <p className="iss-benefits__eyebrow">{data.eyebrow}</p>
        <h2 id="intro-title" className="iss-benefits__title">{data.title}</h2>
        <p className="iss-benefits__lead">{data.lead}</p>
      </header>

      <ul className="iss-benefits__grid">
        {data.cards.map((card) => (
          <li key={card.id} className="iss-benefits__card">
            <span className="iss-benefits__icon"><ISSBenefitIcon kind={card.kind} /></span>
            <h3 className="iss-benefits__card-title">{card.title}</h3>
            <p className="iss-benefits__description">{card.description}</p>
            <span className="iss-benefits__card-index">{card.id} / {card.label}</span>
          </li>
        ))}
      </ul>

      <div className="iss-benefits__actions">
        <a className="iss-benefits__button" href="#iss-equipment-section">
          {data.button} <ArrowRight width={17} height={17} strokeWidth={1.65} aria-hidden="true" focusable="false" />
        </a>
      </div>
    </div>
  );
}

/**
 * Section 02 uniquement : les anciens diagrammes, parcours, tableaux,
 * manuels et CTA ont été retirés de l'affichage pour éviter les doublons.
 * Les textes, cartes, liens et styles validés restent identiques.
 */
export default function TelealarmeISSIntro() {
  const { i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase();
  const benefits = ISS_BENEFITS[lang.startsWith("fr") ? "fr" : "en"];

  return (
    <section className="product-intro" aria-labelledby="intro-title">
      <div className="product-intro-outer">
        <article className="product-intro-card">
          <ISSBenefits data={benefits} />
        </article>
      </div>
    </section>
  );
}
